// PostHog session-replay + product analytics for the web app.
//
// Coexists with Sentry (errors). Both run independently; failure of one
// must not affect the other.
//
// Privacy posture (matches mobile):
// - Session recording masks all <input>, <textarea>, password fields by default
// - We add `data-ph-no-capture` className on chat bubbles, ID upload screens,
//   photo viewers to extend masking to non-input UI containing PII
// - person_profiles: 'identified_only' — anonymous browsers don't get profiles
//
// Kill switch: leave REACT_APP_POSTHOG_KEY unset and SDK becomes a no-op.

import posthog from "posthog-js";

let _initialized = false;

/**
 * Initialise the SDK. Safe to call multiple times — second call is a no-op.
 * Reads `REACT_APP_POSTHOG_KEY` and `REACT_APP_POSTHOG_HOST` from build env.
 * Failures are logged to console but never thrown (telemetry must not crash UI).
 */
export function initPostHog() {
  if (_initialized) return;

  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";

  if (!apiKey) {
    // Silent on the public bundle in production; verbose only in dev.
    if (process.env.NODE_ENV !== "production") {
      console.info("[PostHog] no key configured — telemetry disabled");
    }
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: host,
      person_profiles: "identified_only",
      // `true` is the LEGACY meaning in posthog-js — one pageview on initial
      // load and nothing on SPA route changes. We're a react-router app, so
      // every in-app navigation would go unrecorded. 'history_change' is the
      // explicit opt-in to pushState/replaceState capture.
      capture_pageview: "history_change",
      capture_pageleave: true,
      // Session replay defaults to opt-in; enable explicitly with privacy
      // masking on. PostHog masks all <input> by default; we extend with
      // `maskTextSelector` to also mask the chat bubble + photo viewer DOM.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-no-capture], .chat-message-content, .private-photo, .otp-input, .id-image",
        blockSelector: ".id-upload-area, .verification-photo",
        // Carried over from the inline snippet this replaced.
        recordCrossOriginIframes: true,
      },
      // Autocapture OFF (2026-09-29). It produced 70,411 events in 14 days —
      // 16.3% of a free-plan quota that was running at 83% — from the web
      // client alone, which is the minority surface. Autocapture records that
      // a `div.flex > button` was clicked; it cannot say what the member was
      // trying to do, so nothing in the dashboards reads it. The named events
      // beside it (`contact_requested`, `form_step_completed`,
      // `conversation_opened`) answer the questions actually asked.
      //
      // Turning it back on is one line. What it must not be is the thing
      // quietly consuming the headroom that session replay needs.
      autocapture: false,
      loaded: (ph) => {
        if (process.env.NODE_ENV !== "production") {
          ph.debug();
        }
      },
    });
    _initialized = true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[PostHog] init failed", err);
  }
}

/**
 * Tag the session with a user ID after login. Mirrors the mobile
 * AnalyticsService.setUserProperties contract.
 */
export function identifyUser(userId, properties = {}) {
  if (!_initialized) return;
  try {
    posthog.identify(userId, properties);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[PostHog] identify failed", err);
  }
}

/**
 * Capture a custom event. Same fire-and-forget posture as mobile.
 */
export function captureEvent(name, props = {}) {
  if (!_initialized) return;
  try {
    posthog.capture(name, props);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[PostHog] capture failed", name, err);
  }
}

/**
 * Reset on logout — clears identified user, generates new anonymous ID.
 */
export function resetPostHog() {
  if (!_initialized) return;
  try {
    posthog.reset();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[PostHog] reset failed", err);
  }
}

export { posthog };
